import { SITE_ORIGIN } from "./seo";

/**
 * Every client-side URL and logo decision the chat widgets make, and nothing else.
 *
 * ─── IMPORT BUDGET: `./seo`, BY RELATIVE PATH, AND NOTHING ELSE ───────────────────────────────
 *
 * `frontend/vitest.config.ts` runs with `environment: 'node'` and configures NO `@` alias, so an
 * `@/lib/seo` specifier here would make this module unloadable by the runner and therefore
 * untestable. `seo.ts` itself has zero imports, so a relative import of it is safe. Nothing from
 * React, from any component, or from `@/contexts` may be added: that is the same zero-import rule
 * `chatCopy.ts`, `suggestions.ts` and `chatTurnPolicy.ts` already argue for themselves, and it is
 * the single property that lets the SECURITY-RELEVANT half of the store carousel be unit tested at
 * all. Everything under `__tests__/chatWidgetUrl.test.ts` drives the real exported functions with
 * no DOM and no mocks because of it.
 *
 * ─── NOTHING HERE MAY EVER THROW ──────────────────────────────────────────────────────────────
 *
 * `ChatBlockRenderer`'s own docblock states that a render failing inside the message list blanks the
 * whole panel (T-38-87). This module is called from that render path, so every function below is
 * TOTAL: malformed, hostile and absent input all answer with `undefined` or an empty array.
 */

/**
 * The ImageKit transformation every card logo is served with.
 *
 * 112 is twice the 56px logo box the card renders at, so the asset is crisp on a retina screen, and
 * `c-maintain_ratio` stops a wide wordmark being cropped to a square.
 *
 * THIS IS A URL-ENDPOINT QUERY TRANSFORMATION ON AN ASSET ALREADY HOSTED AT IMAGEKIT, not a
 * web-proxy remote fetch over an arbitrary scraped host. That distinction is the whole design: a
 * remote-fetch origin would need ImageKit dashboard configuration this phase cannot perform, and it
 * would create an open image proxy pointed at attacker-influenceable scraped URLs.
 */
export const CARD_LOGO_TRANSFORM = "w-112,h-112,c-maintain_ratio";

/**
 * The most cards one `stores` block may render.
 *
 * Mirrors `ENVELOPE_BOUNDS.maxItemsPerBlock` in the `chat-agent` workspace, and is RE-DECLARED
 * rather than imported because the two workspaces have separate builds and the frontend cannot
 * import from the agent. The cap is the client half of T-39-27: an oversized `items` array cannot
 * turn one block into a hundred DOM nodes.
 */
export const MAX_CARDS = 10;

/**
 * One store card, as the renderer needs it.
 *
 * `href` IS A SAME-ORIGIN PATH AND NOT THE ABSOLUTE URL THAT ARRIVED. `components/Link.tsx` renders
 * a plain `<a href>` that Vike's client router intercepts, and a same-origin path is the form that
 * reliably stays inside the SPA — an absolute `https://yetfarrag.com/...` invites a full document
 * load, which would tear down the very panel D-07 wants reopened on arrival.
 */
export type ChatStoreCard = {
  storeId: string;
  title: string;
  subtitle?: string;
  /** A same-origin path, e.g. `/store/breadfast`. Never absolute — see the type docblock. */
  href: string;
  /** An `https://ik.imagekit.io` URL carrying exactly one `tr` query. Absent means render initials. */
  logoSrc?: string;
  rating?: number;
  reviewCount?: number;
  /**
   * The contact icon row (39-08). Each is re-validated here — the client half of the same
   * two-place rule `safeWidgetHref` explains — and each is a CONSTRUCTED destination: `tel:` plus
   * E.164 digits, the literal `wa.me` host with a digits-only path, the literal `www.google.com`
   * host on the `/maps` path. A scraped URL is never passed through to any of them.
   */
  telHref?: string;
  whatsappHref?: string;
  mapsHref?: string;
};

/** The one allowed host, derived from the single site-origin constant rather than re-spelled. */
const ALLOWED_HOSTNAME = new URL(SITE_ORIGIN).hostname;

/** The only remote image host the site's own CSP `img-src` permits. */
const IMAGEKIT_HOSTNAME = "ik.imagekit.io";

/**
 * D-14's SECOND enforcement place: turn a card URL into a same-origin path, or refuse it.
 *
 * WHY THIS EXISTS EVEN THOUGH THE SERVER ALREADY REFUSED A BAD URL AT THE PERSISTENCE GATE. A card
 * can arrive from a turn persisted BEFORE that gate existed (the validator's URL checks were
 * length-only until this phase), or from any future producer that writes to `chatturns` without
 * going through it. D-14 is explicit that the rule lives in both places, and this is the half that
 * decides what a live browser actually navigates to.
 *
 * Requires `https:` and a `hostname` STRICTLY EQUAL to the apex. Apex only, per D-13: no subdomain
 * wildcard, no social-platform list, and never a value read out of a store record.
 */
export const safeWidgetHref = (value: unknown): string | undefined => {
  if (typeof value !== "string" || value.trim() === "") return undefined;

  let parsed: URL;
  try {
    parsed = new URL(value.trim());
  } catch {
    // Relative (`/store/x`) and malformed values throw here, and both are correctly refused: a
    // validated envelope stores its URLs absolute precisely so they can be host-checked.
    return undefined;
  }

  if (parsed.protocol !== "https:") return undefined;

  // `hostname`, and NEVER a `startsWith` prefix test of `href` against the origin. Such a test passes
  // `https://yetfarrag.com.evil.com/store/x`, which is a different site entirely. `hostname` also
  // excludes the port, so the comparison is about trust rather than punctuation.
  if (parsed.hostname !== ALLOWED_HOSTNAME) return undefined;

  // The hash is dropped deliberately: nothing in a store route uses one, and it is the one component
  // an attacker could still shape once the scheme and host are pinned.
  const href = parsed.pathname + parsed.search;

  // A protocol-relative href (`//evil.com/...`) leaves the origin, and `new URL('https://apex//x')`
  // really does produce a pathname of `//x`. Guarding here is what keeps the return type's promise.
  if (!href.startsWith("/") || href.startsWith("//")) return undefined;

  return href;
};

/**
 * D-16's client half: the only image source a card is ever allowed to request.
 *
 * ONE HOST, AND IT IS THE ONLY ONE THAT RENDERS. The site's own CSP `img-src`
 * (`backend/src/middleware/securityHeaders.ts:72`) permits `'self'`, `data:`, `ik.imagekit.io` and
 * `*.googleusercontent.com` and nothing else, so any other host is unrenderable under enforcement no
 * matter what this function returns. Loading one anyway would disclose the visitor's IP and Referer
 * to an arbitrary scraped host on every render (T-39-25), for an image that would then be blocked.
 *
 * THE MEASURED COST OF THE RESTRICTION, verified live against the production database on 2026-08-29
 * over 1,792 published stores: 1,670 (93.2%) already carry an `https://ik.imagekit.io/` `logoUrl`,
 * 34 carry some other https host (dominated by expiring `scontent.*.fbcdn.net` URLs that 403 weeks
 * after a scrape — the dead-logo class `backend/src/services/imageService.ts` documents), and 88
 * carry nothing usable (a non-https value, overwhelmingly the literal string "null", or nothing at
 * all). So this costs about 2 percent of cards an image they could never have rendered, not "a wall
 * of initials". The 88 have no image to lose under any design.
 *
 * `searchParams.set` and NEVER string concatenation: `set` is what makes a URL that already carries
 * a `tr` query a REPLACE rather than a duplicate, and a duplicated `tr` is an ambiguous request.
 */
export const chatCardLogoSrc = (value: unknown): string | undefined => {
  if (typeof value !== "string" || value.trim() === "") return undefined;

  let parsed: URL;
  try {
    parsed = new URL(value.trim());
  } catch {
    // `"null"`, the empty-ish leftovers of a scrape, and every relative value land here.
    return undefined;
  }

  if (parsed.protocol !== "https:") return undefined;
  if (parsed.hostname !== IMAGEKIT_HOSTNAME) return undefined;

  parsed.searchParams.set("tr", CARD_LOGO_TRANSFORM);
  return parsed.toString();
};

/** A string that carries something. Used for every required and optional text field below. */
const nonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim() !== "";

/** Review quality, 0 to 5. NOT `yetfarragRating`, which is store size on a different scale. */
const isValidRating = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 5;

const isValidReviewCount = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

// ─── The contact trio's client-side gates (39-08) ─────────────────────────────────────────────
//
// Mirrors `chat-agent/src/message/widgetUrl.ts`'s contact section, re-declared for the same
// two-workspace reason as `MAX_CARDS`. Each function is TOTAL and answers `undefined` for anything
// it does not trust, so a pre-gate turn or a hostile payload costs an icon, never a render.

/** `+` then 7 to 15 digits — the exact shape the server's persistence gate stores. */
const isCardPhone = (value: unknown): value is string =>
  typeof value === "string" && /^\+\d{7,15}$/.test(value);

/** A `tel:` href from a validated card phone, or nothing. */
export const safeTelHref = (value: unknown): string | undefined =>
  isCardPhone(value) ? `tel:${value}` : undefined;

/** The literal `wa.me` host with a digits-only path, or nothing. */
export const safeWhatsappHref = (value: unknown): string | undefined => {
  if (typeof value !== "string" || value.length === 0 || value.length > 2048) return undefined;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "https:") return undefined;
    if (parsed.hostname !== "wa.me") return undefined;
    if (!/^\/\d{7,15}$/.test(parsed.pathname)) return undefined;
    return `https://wa.me${parsed.pathname}`;
  } catch {
    return undefined;
  }
};

/** The literal `www.google.com` host on the `/maps` path, or nothing. */
export const safeMapsHref = (value: unknown): string | undefined => {
  if (typeof value !== "string" || value.length === 0 || value.length > 2048) return undefined;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "https:") return undefined;
    if (parsed.hostname !== "www.google.com") return undefined;
    if (!parsed.pathname.startsWith("/maps")) return undefined;
    return parsed.toString();
  } catch {
    return undefined;
  }
};

/**
 * Narrow a wire `items` payload into renderable cards, dropping whatever cannot be trusted.
 *
 * D-15: A URL THAT FAILS THE ALLOWLIST DROPS THAT ITEM AND THE REST OF THE CAROUSEL RENDERS. That
 * composes with the server's D-02 silent drop of unresolvable IDs, so the system has exactly ONE
 * "omit the bad item" rule rather than two behaviours. Collapsing the whole block to its text
 * fallback was rejected: our URLs are built from slugs on our own origin, so the likeliest cause of
 * one bad URL is a bug affecting all of them anyway.
 *
 * `rating` and `reviewCount` are kept ONLY WHEN BOTH VALIDATE, and omitted together otherwise. A
 * rating with no count and a count with no rating are both half a claim, and keeping them coupled
 * leaves the renderer's D-06 rating-row decision a single question rather than two.
 *
 * Returns an empty array for anything that is not an array, and for an array whose every member is
 * unusable — the caller then falls back to the block's own `text`, which is a valid outcome and not
 * an error (T-39-26). This function never throws.
 */
export const toSafeCards = (items: unknown): ChatStoreCard[] => {
  if (!Array.isArray(items)) return [];

  const cards: ChatStoreCard[] = [];

  for (const candidate of items) {
    if (cards.length >= MAX_CARDS) break;
    if (candidate === null || typeof candidate !== "object") continue;

    const { storeId, title, subtitle, imageUrl, url, rating, reviewCount, phone, whatsappUrl, mapsUrl } =
      candidate as {
        storeId?: unknown;
        title?: unknown;
        subtitle?: unknown;
        imageUrl?: unknown;
        url?: unknown;
        rating?: unknown;
        reviewCount?: unknown;
        phone?: unknown;
        whatsappUrl?: unknown;
        mapsUrl?: unknown;
      };

    if (!nonEmptyString(storeId) || !nonEmptyString(title)) continue;

    const href = safeWidgetHref(url);
    if (href === undefined) continue;

    const logoSrc = chatCardLogoSrc(imageUrl);
    const hasRatingPair = isValidRating(rating) && isValidReviewCount(reviewCount);
    const telHref = safeTelHref(phone);
    const whatsappHref = safeWhatsappHref(whatsappUrl);
    const mapsHref = safeMapsHref(mapsUrl);

    // Conditional spread on every optional key, so an absent value is an ABSENT KEY rather than an
    // `undefined` one: the renderer asks `in`/truthiness questions, not `!== undefined` ones.
    cards.push({
      storeId,
      title,
      href,
      ...(nonEmptyString(subtitle) ? { subtitle } : {}),
      ...(logoSrc === undefined ? {} : { logoSrc }),
      ...(hasRatingPair ? { rating: rating as number, reviewCount: reviewCount as number } : {}),
      ...(telHref === undefined ? {} : { telHref }),
      ...(whatsappHref === undefined ? {} : { whatsappHref }),
      ...(mapsHref === undefined ? {} : { mapsHref }),
    });
  }

  return cards;
};
